import { useCallback, useEffect } from "react";
import { FormProvider, get, useForm } from "react-hook-form";

import type { z } from "zod";
import { produce } from "immer";
import debounce from "lodash.debounce";

import { t } from "@lingui/macro";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResumeStore } from "@/client/stores/resume";
import { AiActions } from "@/client/components/ai-actions";
import { T } from "@lingui/react/dist/shared/react.e5f95de8";
import { useDialog, DialogName } from "@/client/stores/dialog";
import { useSaveStatusStore } from "@/client/stores/useSaveStatusStore";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import { defaultAward, awardSchema, SectionWithItem } from "@reactive-resume/schema";

import { useAutosaveStatus } from "../../../_components/SavingIndicator";

import { URLInput } from "../sections/shared/url-input";

const formSchema = awardSchema;

type FormValues = z.infer<typeof formSchema>;

export const AwardsForm = ({ id }: { id: string }) => {
  const { payload } = useDialog<T>(id as DialogName);

  const form = useForm<FormValues>({
    defaultValues: { ...defaultAward, ...payload?.item },
    resolver: zodResolver(formSchema),
  });

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  const onSubmit = (values: T) => {
    if (!section) return;

    if (!payload.item?.id) return;

    setValue(
      `sections.${id}.items`,
      produce(section.items, (draft: T[]): void => {
        const index = draft.findIndex((item) => item.id === payload.item?.id);
        if (index === -1) return;
        draft[index] = values;
      }),
    );
  };

  const { status, triggerSave } = useAutosaveStatus({
    saveFn: async () => {
      const values = form.getValues();
      onSubmit(values);
    },
    debounceMs: 500,
    minSavingMs: 300,
    savedVisibleMs: 1200,
  });

  const setStatus = useSaveStatusStore((s) => s.setStatus);

  useEffect(() => {
    setStatus(status);
  }, [status, setStatus]);

  const debouncedSubmit = useCallback(
    debounce(() => {
      triggerSave(); // notify status hook
    }, 500),
    [form, triggerSave],
  );

  return (
    <FormProvider {...form}>
      <form className="max-h-[60vh] space-y-6 lg:max-h-fit" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 p-1 sm:grid-cols-2">
          <FormField
            name="title"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t({ message: "Title", context: "Name of the Award" })}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="awarder"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Awarder`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="date"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Date`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    placeholder={t({
                      message: "March 2023",
                      comment: "The month and year should be uniform across all languages.",
                    })}
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="url"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Website`}</FormLabel>
                <FormControl>
                  <URLInput
                    {...field}
                    onChange={(value) => {
                      field.onChange(value);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="summary"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t`Summary`}</FormLabel>
                <FormControl>
                  <RichInput
                    {...field}
                    content={field.value}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        onChange={(value) => {
                          editor.commands.setContent(value, true);
                          field.onChange(value);
                          debouncedSubmit();
                        }}
                      />
                    )}
                    className="bg-white"
                    onChange={(value) => {
                      field.onChange(value);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </FormProvider>
  );
};
